import { Component, DestroyRef, inject, Input, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormsModule, FormGroup } from '@angular/forms';
import { IssueAfter2017 } from '../../../../shared/components/issue-detail-edit/issue-after-2017/issue-after-2017';
import { IssuePrior2018 } from '../../../../shared/components/issue-detail-edit/issue-prior-2018/issue-prior-2018';
import { AppIssuesService } from '../../../../core/services/issues.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AppModalService } from '../../../../core/services/app-modal.service';
import {
  AmendedDetailDTO,
  AuditPrior2018DTO,
  ExistingIssueDetailsDTO,
  ExistingIssueRequestDTO,
  IssueIdLookupDTO,
  Prior2018DTO,
  UpdateFilterIssueRequestDTO,
} from '../../../../core/interface/all-issues.interface';
import { EntityDTO } from '../../../../core/interface/maintenance.interface';
import { GenericTableService } from '../../../../core/services/app-generic-table.service';
import { YES_NO_OPTIONS } from '../../../../core/constants/issues.constant';
import { DropdownOptionDTO } from '../../../../core/interface/app-common.interface';
import { AppCommonService } from '../../../../core/services/app-common.service';
import {
  After2017RecordDTO,
  IssuePrior2018RecordDTO,
} from '../../../../core/interface/issues-2017-2018.interface';
import { emptyIssuePrior2018Record } from '../../../../core/constants/issues-2017-2018.constants';
import { ToastService } from '../../../../core/services/app-toast.service';
import { CreateAuditTrailDTO } from '../../../../core/interface/audit-trail.interface';
import { MISSING_REQUIRED_FIELDS_MESSAGE } from '../../../../core/constants/app-common.constants';

@Component({
  selector: 'app-existing-issue-content',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, IssuePrior2018, IssueAfter2017],
  templateUrl: './existing-issue-content.html',
  styleUrl: './existing-issue-content.scss',
})
export class ExistingIssueContent implements OnInit {
  @Input() mode: 'existing' | 'record-edit' = 'existing';
  @Input() action: 'view' | 'edit' = 'view';
  @Input() entityList: EntityDTO[] = [];
  @Input() existingIssueForm!: FormGroup;
  @Input() isSaveVisible = true;
  yesNoOptions: DropdownOptionDTO[] = YES_NO_OPTIONS;
  issueIdResponse$ = signal<IssueIdLookupDTO[]>([]);
  amendedDetails$ = signal<AmendedDetailDTO[]>([]);
  entitiesResponse$ = signal<EntityDTO[]>([]);
  prior2018Records: IssuePrior2018RecordDTO[] = [structuredClone(emptyIssuePrior2018Record)];
  auditPrior2018Records: IssuePrior2018RecordDTO[] = [structuredClone(emptyIssuePrior2018Record)];
  after2017Records: After2017RecordDTO[] = [this.createEmptyAfter2017Record()];
  auditAfter2017Records: After2017RecordDTO[] = [this.createEmptyAfter2017Record()];
  private originalPayload?: ExistingIssueRequestDTO;
  deduction199 = false;
  private loadedArFilingYear: number | null = null;
  private requestedArFilingYear: number | null = null;
  private isArFilingLoading = false;
  activeTab = 'prior2018';
  createdDate: string = '';
  modifiedDate: string = '';
  arFilingPlaceholder = 'Select Issue Year above to load AR Filing IDs into dropdown';

  private readonly destroyRef = inject(DestroyRef);
  private readonly appModalService = inject(AppModalService);
  private readonly issuesService = inject(AppIssuesService);
  private readonly genericTableService = inject(GenericTableService);
  private readonly commonService = inject(AppCommonService);
  private readonly toastService = inject(ToastService);

  ngOnInit(): void {
    const form = this.existingIssueForm.controls;

    this.loadLookupData();
    this.getEntities();
    this.subscribeToIssueYearChanges();

    if (this.mode === 'existing') {
      this.initializeExistingModeTables();
      form['issueId'].enable();
      form['description'].enable();
      return;
    }

    this.subscribeToFilterEditData();

    if (this.mode === 'record-edit') {
      form['issueId'].disable();
      form['description'].disable();

      this.initializeEditArFilingId();
    }
  }

  private initializeEditArFilingId(): void {
    const issueYearValue = String(this.existingIssueForm.get('issueYear')?.value ?? '').trim();

    const existingArFilingId = String(this.existingIssueForm.get('arFilingId')?.value ?? '').trim();

    if (!/^\d{4}$/.test(issueYearValue) || !existingArFilingId) {
      return;
    }

    this.onArFilingDropdownClick(existingArFilingId);
  }

  private initializeExistingModeTables(): void {
    this.activeTab = 'prior2018';
    this.prior2018Records = [structuredClone(emptyIssuePrior2018Record)];
    this.auditPrior2018Records = [structuredClone(emptyIssuePrior2018Record)];
    this.after2017Records = [this.createEmptyAfter2017Record()];
    this.auditAfter2017Records = [this.createEmptyAfter2017Record()];
    this.originalPayload = undefined;
    this.createdDate = '';
    this.modifiedDate = '';
  }

  private applyCreateAdjustmentContext(): void {
    const context = this.issuesService.getExistingIssueContext();
    if (context?.mode !== 'CREATE_ADJUSTMENT') {
      return;
    }
    this.resetCreateAdjustmentForm();
    this.existingIssueForm.patchValue(
      {
        issueId: context.issueId,
        description: context.description,
      },
      {
        emitEvent: false,
      },
    );
    this.existingIssueForm.get('description')?.disable({
      emitEvent: false,
    });
    this.existingIssueForm.markAsPristine();
    this.existingIssueForm.markAsUntouched();
    this.issuesService.clearExistingIssueContext();
  }

  private resetCreateAdjustmentForm(): void {
    this.existingIssueForm.reset(
      {
        id: '',
        issueId: '',
        description: '',
        entityCode: '',
        issueYear: null,
        epAdjRequired: '',
        contact: '',
        approver: '',
        tbbsAdjRequired: '',
        cfcNc902: '',
        dateReviewed: '',
        dateCorptaxAdj: '',
        adjDescrip: '',
        arFilingId: '',
        arIssueId: '',
        irsIssueID: '',
        saa: '',
        submission: '',
        spDocLink: '',
        npa: '',
        npaTiesToArSaa: '',
        issueStatus: 'Active',
        deduction199: false,
        creationDate: null,
        modificationDate: null,
      },
      {
        emitEvent: false,
      },
    );
    this.loadedArFilingYear = null;
    this.requestedArFilingYear = null;
    this.isArFilingLoading = false;
    this.arFilingPlaceholder = 'Select Issue Year above to load AR Filing IDs into dropdown';
    this.amendedDetails$.set([]);
  }

  private subscribeToFilterEditData(): void {
    this.appModalService.existingIssueAllDataState$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: any) => {
        if (!data) {
          return;
        }
        this.setEditIssueData(data);
      });
  }

  /** Filter Action: When Issue-ID Selected */
  onIssueChange(event: any): void {
    const issueIdControl = this.existingIssueForm.get('issueId');
    const descriptionControl = this.existingIssueForm.get('description');
    const issueId = issueIdControl?.value;
    if (!issueId) {
      descriptionControl?.reset('', {
        emitEvent: false,
      });
      descriptionControl?.enable({
        emitEvent: false,
      });
      return;
    }

    this.genericTableService
      .getIssueByIssueId(issueId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          const description = response.result?.description ?? '';
          descriptionControl?.setValue(description, {
            emitEvent: false,
          });
          descriptionControl?.disable({
            emitEvent: false,
          });
        },
        error: (error) => {
          console.error('Get issue description API error:', error);
          descriptionControl?.reset('', {
            emitEvent: false,
          });
          descriptionControl?.enable({
            emitEvent: false,
          });
          this.toastService.error('Unable to load issue description.');
        },
      });
  }

  /** GET; IssueI & Filing_ID List  */
  private loadLookupData(): void {
    this.issuesService
      .getFiltersCached()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          if (!response.success) {
            return;
          }
          const result = response.result;
          this.issueIdResponse$.set(result.issueIds);
          this.applyCreateAdjustmentContext();
        },
        error: (error) => {
          console.error('getFilters API Error', error);
        },
      });
  }

  /** API::GET: ENTITIES */
  getEntities(): void {
    this.genericTableService
      .loadEntities()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          const entities = response.result ?? [];
          this.entitiesResponse$.set(entities);
        },
      });
  }

  private setEditIssueData(data: any): void {
    if (this.mode !== 'record-edit') {
      return;
    }
    const form = this.existingIssueForm.controls;
    this.createdDate = this.commonService.formatDisplayDate(form['creationDate']?.value) ?? '';
    this.modifiedDate = this.commonService.formatDisplayDate(form['modificationDate']?.value) ?? '';
    this.prior2018Records = data?.incomeCredit2018?.length
      ? structuredClone(data.incomeCredit2018)
      : [structuredClone(emptyIssuePrior2018Record)];
    this.auditPrior2018Records = data?.auditChanges2018?.length
      ? structuredClone(data.auditChanges2018)
      : [structuredClone(emptyIssuePrior2018Record)];
    this.after2017Records = this.normalizeAfter2017Records(data?.incomeCreditAfter2017, false);
    this.auditAfter2017Records = this.normalizeAfter2017Records(data?.auditChangesAfter2017, true);
    this.originalPayload = structuredClone(this.buildPayload(false));
  }

  private normalizeAfter2017Records(
    recordData: any[] | null | undefined,
    isAudit: boolean,
  ): After2017RecordDTO[] {
    if (!recordData?.length) {
      return [this.createEmptyAfter2017Record()];
    }
    return recordData.map((issue: any): After2017RecordDTO => {
      if (Array.isArray(issue.rows) && issue.rows.length > 0) {
        return structuredClone(issue);
      }
      return {
        id: issue.id ?? 0,
        issueId: issue.issueID ?? '',
        year: issue.issueYear ?? 0,
        entity: issue.entityCode ?? '',
        cfc: issue.cfc ?? '',
        isCancelled: issue.irsIssueID?.trim().toLowerCase() === 'cancel',
        isFilled: issue.status?.trim().toLowerCase() === 'filed',

        rows: [
          {
            label: 'TI b/f 199',
            us: this.toGridValue(isAudit ? issue.audit_TI_US_2020 : issue.tI_US_2020),
            branchNogi: this.toGridValue(isAudit ? issue.audit_TI_Br_NOGI : issue.tI_Br_NOGI),
            branchCfogi: this.toGridValue(isAudit ? issue.audit_TI_Br_CFOGI : issue.tI_Br_CFOGI),
            nonBranchNogi: this.toGridValue(isAudit ? issue.audit_TI_Nbr_NOGI : issue.tI_Nbr_NOGI),
            nonBranchCfogi: this.toGridValue(
              isAudit ? issue.audit_TI_NBr_CFOGI : issue.tI_NBr_CFOGI,
            ),
            passive: this.toGridValue(
              isAudit ? issue.audit_TI_Passive_2020 : issue.tI_Passive_2020,
            ),
            giltiNogi: this.toGridValue(isAudit ? issue.audit_TI_GILTI_NOGI : issue.tI_GILTI_NOGI),
            giltiFori: this.toGridValue(isAudit ? issue.audit_TI_GILTI_FORI : issue.tI_GILTI_FORI),
            capitalGain: this.toGridValue(
              isAudit ? issue.audit_TI_CapitalGain_2020 : issue.tI_CapitalGain_2020,
            ),
            npaLast: false,
          },

          {
            label: '',
            us: '',
            branchNogi: '',
            branchCfogi: '',
            nonBranchNogi: '',
            nonBranchCfogi: '',
            passive: '',
            giltiNogi: '',
            giltiFori: '',
            capitalGain: '',
            npaLast: false,
          },

          {
            label: 'FTC',
            us: '',
            branchNogi: this.toGridValue(isAudit ? issue.audit_FTC_Br_NOGI : issue.ftC_Br_NOGI),
            branchCfogi: this.toGridValue(isAudit ? issue.audit_FTC_Br_CFOGI : issue.ftC_Br_CFOGI),
            nonBranchNogi: this.toGridValue(
              isAudit ? issue.audit_FTC_Nbr_NOGI : issue.ftC_Nbr_NOGI,
            ),
            nonBranchCfogi: this.toGridValue(
              isAudit ? issue.audit_FTC_NBr_CFOGI : issue.ftC_NBr_CFOGI,
            ),
            passive: this.toGridValue(
              isAudit ? issue.audit_FTC_Passive_2020 : issue.ftC_Passive_2020,
            ),
            giltiNogi: this.toGridValue(
              isAudit ? issue.audit_FTC_GILTI_NOGI : issue.ftC_GILTI_NOGI,
            ),
            giltiFori: this.toGridValue(
              isAudit ? issue.audit_FTC_GILTI_FORI : issue.ftC_GILTI_FORI,
            ),
            capitalGain: '',
            npaLast: false,
          },

          {
            label: 'GBC',
            us: this.toGridValue(
              isAudit
                ? (issue.audit_GBC_After_2017 ?? issue.audit_GBC)
                : (issue.gbC_After_2017 ?? issue.gbc),
            ),
            branchNogi: '',
            branchCfogi: '',
            nonBranchNogi: '',
            nonBranchCfogi: '',
            passive: '',
            giltiNogi: '',
            giltiFori: '',
            capitalGain: '',
            npaLast: false,
          },

          {
            label: '',
            us: '',
            branchNogi: '',
            branchCfogi: '',
            nonBranchNogi: '',
            nonBranchCfogi: '',
            passive: '',
            giltiNogi: '',
            giltiFori: 'NPA ties AR/SAA',
            capitalGain: this.toGridValue(issue.isNPATied),
            npaLast: true,
          },
        ],

        arFilingId: issue.amendedID ?? issue.arFilingId ?? null,
        arIssueId: issue.arIssueID ?? issue.arIssueId ?? null,
        irsIssueID: issue.irsIssueID ?? null,
        status: issue.status ?? null,
        saaID: issue.saA_ID ?? issue.saaID ?? null,
        saaSubmission: issue.saA_Submission ?? issue.saaSubmission ?? null,
        docID: issue.sP_DocID ?? issue.docID ?? null,
        npa: issue.npa ?? null,
        saaChecked: issue.asC_740Flag ?? issue.saaChecked ?? false,
        epAdjText: 'ASC 740',
        isEPAdjRequired: issue.isEPAdjRequired ?? null,
        isTBBSAdjRequired: issue.isTBBSAdjRequired ?? null,
        dateCorptaxAdj: issue.dateCorptaxAdj
          ? this.commonService.formatDisplayDate(String(issue.dateCorptaxAdj))
          : null,
      };
    });
  }

  private toGridValue(value: unknown): string {
    if (value === null || value === undefined || value === '') {
      return '';
    }
    return String(value);
  }

  selectTab(tabId: string): void {
    this.activeTab = tabId;
  }

  private buildAuditTrail(
    original: ExistingIssueRequestDTO,
    current: ExistingIssueRequestDTO,
  ): CreateAuditTrailDTO[] {
    const auditDateTime = this.commonService.createISOTimeZone(new Date().toString());
    const currentUser = JSON.parse(sessionStorage.getItem('currentUser') ?? '{}');
    const userName: string = currentUser.emailId ?? '';
    const currentKeys = Object.keys(current) as Array<keyof ExistingIssueRequestDTO>;
    return currentKeys.reduce<CreateAuditTrailDTO[]>((auditTrail, fieldName) => {
      if (fieldName === 'dateCreation' || fieldName === 'dateModification') {
        return auditTrail;
      }
      const oldValue = original[fieldName];
      const newValue = current[fieldName];
      if (this.areAuditValuesEqual(oldValue, newValue)) {
        return auditTrail;
      }
      auditTrail.push({
        auditTrailID: 0,
        auditDateTime,
        userName,
        formName: 'filtersub_IssueEffect',
        recordID: String(current.id) ?? '',
        fieldName: String(fieldName),
        oldValue: this.toAuditValue(oldValue),
        newValue: this.toAuditValue(newValue),
      });
      return auditTrail;
    }, []);
  }

  private areAuditValuesEqual(oldValue: unknown, newValue: unknown): boolean {
    if (typeof oldValue === 'number' || typeof newValue === 'number') {
      return this.parseAmount(oldValue) === this.parseAmount(newValue);
    }
    return String(oldValue ?? '').trim() === String(newValue ?? '').trim();
  }

  private toAuditValue(value: unknown): string {
    if (value === null || value === undefined) {
      return '';
    }
    if (typeof value === 'object') {
      return JSON.stringify(value);
    }
    return String(value);
  }

  public saveRecord(isEdit: boolean = false): void {
    this.existingIssueForm.updateValueAndValidity({
      emitEvent: false,
    });

    if (this.existingIssueForm.invalid) {
      this.existingIssueForm.markAllAsTouched();
      this.existingIssueForm.markAsTouched();
      this.toastService.error(MISSING_REQUIRED_FIELDS_MESSAGE);
      return;
    }
    const issueId = this.existingIssueForm.get('issueId')?.value ?? '';
    const issuePayload = this.buildPayload();

    if (this.isSaveVisible) {
      this.issuesService.saveOrUpdateExistingIssued(issuePayload, issueId);
      return;
    }

    if (!this.originalPayload) {
      this.toastService.error('Original issue data is not available.');
      return;
    }

    const auditTrail = this.buildAuditTrail(this.originalPayload, issuePayload);

    const updatePayload: UpdateFilterIssueRequestDTO = {
      filterIssueEffect: issuePayload,
      auditTrail,
    };

    this.issuesService.updateIssueFilterDetails(updatePayload, issueId, true);
  }

  private buildPayload(updateDisplayDates = true): ExistingIssueRequestDTO {
    return {
      ...this.buildIssueDetailsPayload(updateDisplayDates),
      ...this.buildPrior2018Payload(this.prior2018Records),
      ...this.buildAuditPrior2018Payload(this.auditPrior2018Records),
      ...this.buildAfter2017Payload(this.after2017Records),
      ...this.buildAuditAfter2017Payload(this.auditAfter2017Records),
    };
  }

  private buildIssueDetailsPayload(updateDisplayDates = true): ExistingIssueDetailsDTO {
    const form = this.existingIssueForm.getRawValue();
    const currentDate = this.commonService.createISOTimeZone(new Date().toString());
    let creationDate: string;
    let modificationDate: string;

    if (this.mode === 'existing') {
      creationDate = currentDate;
      modificationDate = currentDate;
    } else {
      creationDate = form.creationDate
        ? this.commonService.createISOTimeZone(String(form.creationDate))
        : currentDate;

      modificationDate = currentDate;
    }

    if (updateDisplayDates) {
      this.createdDate = this.commonService.formatDisplayDate(creationDate);
      this.modifiedDate = this.commonService.formatDisplayDate(modificationDate);
    }

    return {
      id: form.id === '' || form.id === 0 || form.id === null ? 0 : form.id,
      issueID: form.issueId,
      deduction199: Boolean(form.deduction199),
      entityCode: form.entityCode,
      issueYear: Number(form.issueYear),
      isEPAdjRequired: form.epAdjRequired,
      contact: form.contact,
      approver: form.approver,
      isTBBSAdjRequired: form.tbbsAdjRequired,
      cfc: form.cfcNc902,
      dateReviewed: form.dateReviewed
        ? this.commonService.createISOTimeZone(String(form.dateReviewed))
        : null,
      dateCorptaxAdj: form.dateCorptaxAdj
        ? this.commonService.createISOTimeZone(String(form.dateCorptaxAdj))
        : null,
      desc: form.adjDescrip,
      amendedID: form.arFilingId,
      arIssueID: form.arIssueId,
      irsIssueID: form.irsIssueID,
      saA_ID: form.saa,
      saA_Submission: form.submission,
      sP_DocID: form.spDocLink,
      npa: form.npa,
      isNPATied: form.npaTiesToArSaa,
      issue_Status: form.issueStatus,
      dateCreation: creationDate,
      dateModification: modificationDate,
    };
  }

  private buildPrior2018Payload(records: IssuePrior2018RecordDTO[]): Prior2018DTO {
    const rows = records[0]?.rows ?? [];
    const tiRow = rows.find((row) => row.empty === 'TI b/f 199');
    const d199Row = rows.find((row) => row.empty === '199 Deduct');
    const ftcRow = rows.find((row) => row.empty === 'FTC');
    const gbcRow = rows.find((row) => row.empty === 'GBC');
    return {
      tI_US: this.parseAmount(tiRow?.us),
      tI_General: this.parseAmount(tiRow?.general),
      tI_FOGEI: this.parseAmount(tiRow?.fogei),
      tI_FORI: this.parseAmount(tiRow?.fori),
      tI_Passive: this.parseAmount(tiRow?.passive),
      tI_CapitalGain: this.parseAmount(tiRow?.capitalGain),
      d199_US: this.parseAmount(d199Row?.us),
      d199_General: this.parseAmount(d199Row?.general),
      d199_FOGEI: this.parseAmount(d199Row?.fogei),
      d199_FORI: this.parseAmount(d199Row?.fori),
      d199_Passive: this.parseAmount(d199Row?.passive),
      ftC_General: this.parseAmount(ftcRow?.general),
      ftC_FOGEI: this.parseAmount(ftcRow?.fogei),
      ftC_FORI: this.parseAmount(ftcRow?.fori),
      ftC_Passive: this.parseAmount(ftcRow?.passive),
      gbc: this.parseAmount(gbcRow?.us),
    };
  }

  private buildAuditPrior2018Payload(records: IssuePrior2018RecordDTO[]): AuditPrior2018DTO {
    const rows = records[0]?.rows ?? [];
    const tiRow = rows.find((row) => row.empty === 'TI b/f 199');
    const d199Row = rows.find((row) => row.empty === '199 Deduct');
    const ftcRow = rows.find((row) => row.empty === 'FTC');
    const gbcRow = rows.find((row) => row.empty === 'GBC');
    return {
      audit_TI_US: this.parseAmount(tiRow?.us),
      audit_TI_General: this.parseAmount(tiRow?.general),
      audit_TI_FOGEI: this.parseAmount(tiRow?.fogei),
      audit_TI_FORI: this.parseAmount(tiRow?.fori),
      audit_TI_Passive: this.parseAmount(tiRow?.passive),
      audit_TI_CapitalGain: this.parseAmount(tiRow?.capitalGain),
      audit_D199_US: this.parseAmount(d199Row?.us),
      audit_D199_General: this.parseAmount(d199Row?.general),
      audit_D199_FOGEI: this.parseAmount(d199Row?.fogei),
      audit_D199_FORI: this.parseAmount(d199Row?.fori),
      audit_D199_Passive: this.parseAmount(d199Row?.passive),
      audit_FTC_General: this.parseAmount(ftcRow?.general),
      audit_FTC_FOGEI: this.parseAmount(ftcRow?.fogei),
      audit_FTC_FORI: this.parseAmount(ftcRow?.fori),
      audit_FTC_Passive: this.parseAmount(ftcRow?.passive),
      audit_GBC: this.parseAmount(gbcRow?.us),
    };
  }

  private buildAfter2017Payload(records: After2017RecordDTO[]): any {
    const rows = records[0]?.rows ?? [];
    const tiRow = rows.find((row) => row.label === 'TI b/f 199');
    const ftcRow = rows.find((row) => row.label === 'FTC');
    const gbcRow = rows.find((row) => row.label === 'GBC');
    return {
      tI_US_2020: this.parseAmount(tiRow?.us),
      tI_Br_NOGI: this.parseAmount(tiRow?.branchNogi),
      tI_Br_CFOGI: this.parseAmount(tiRow?.branchCfogi),
      tI_Nbr_NOGI: this.parseAmount(tiRow?.nonBranchNogi),
      tI_NBr_CFOGI: this.parseAmount(tiRow?.nonBranchCfogi),
      tI_Passive_2020: this.parseAmount(tiRow?.passive),
      tI_GILTI_NOGI: this.parseAmount(tiRow?.giltiNogi),
      tI_GILTI_FORI: this.parseAmount(tiRow?.giltiFori),
      tI_CapitalGain_2020: this.parseAmount(tiRow?.capitalGain),
      ftC_Br_NOGI: this.parseAmount(ftcRow?.branchNogi),
      ftC_Br_CFOGI: this.parseAmount(ftcRow?.branchCfogi),
      ftC_Nbr_NOGI: this.parseAmount(ftcRow?.nonBranchNogi),
      ftC_NBr_CFOGI: this.parseAmount(ftcRow?.nonBranchCfogi),
      ftC_Passive_2020: this.parseAmount(ftcRow?.passive),
      ftC_GILTI_NOGI: this.parseAmount(ftcRow?.giltiNogi),
      ftC_GILTI_FORI: this.parseAmount(ftcRow?.giltiFori),
      gbC_After_2017: this.parseAmount(gbcRow?.us),
    };
  }

  private buildAuditAfter2017Payload(records: After2017RecordDTO[]): any {
    const rows = records[0]?.rows ?? [];
    const tiRow = rows.find((row) => row.label === 'TI b/f 199');
    const ftcRow = rows.find((row) => row.label === 'FTC');
    const gbcRow = rows.find((row) => row.label === 'GBC');
    return {
      audit_TI_US_2020: this.parseAmount(tiRow?.us),
      audit_TI_Br_NOGI: this.parseAmount(tiRow?.branchNogi),
      audit_TI_Br_CFOGI: this.parseAmount(tiRow?.branchCfogi),
      audit_TI_Nbr_NOGI: this.parseAmount(tiRow?.nonBranchNogi),
      audit_TI_NBr_CFOGI: this.parseAmount(tiRow?.nonBranchCfogi),
      audit_TI_Passive_2020: this.parseAmount(tiRow?.passive),
      audit_TI_GILTI_NOGI: this.parseAmount(tiRow?.giltiNogi),
      audit_TI_GILTI_FORI: this.parseAmount(tiRow?.giltiFori),
      audit_TI_CapitalGain_2020: this.parseAmount(tiRow?.capitalGain),
      audit_FTC_Br_NOGI: this.parseAmount(ftcRow?.branchNogi),
      audit_FTC_Br_CFOGI: this.parseAmount(ftcRow?.branchCfogi),
      audit_FTC_Nbr_NOGI: this.parseAmount(ftcRow?.nonBranchNogi),
      audit_FTC_NBr_CFOGI: this.parseAmount(ftcRow?.nonBranchCfogi),
      audit_FTC_Passive_2020: this.parseAmount(ftcRow?.passive),
      audit_FTC_GILTI_NOGI: this.parseAmount(ftcRow?.giltiNogi),
      audit_FTC_GILTI_FORI: this.parseAmount(ftcRow?.giltiFori),
      audit_GBC_After_2017: this.parseAmount(gbcRow?.us),
    };
  }

  private createEmptyAfter2017Record(): After2017RecordDTO {
    return {
      id: 0,
      issueId: '',
      year: 0,
      entity: '',
      cfc: '',
      isCancelled: false,
      isFilled: false,

      rows: [
        {
          label: 'TI b/f 199',
          us: '',
          branchNogi: '',
          branchCfogi: '',
          nonBranchNogi: '',
          nonBranchCfogi: '',
          passive: '',
          giltiNogi: '',
          giltiFori: '',
          capitalGain: '',
        },
        {
          label: '',
          us: '',
          branchNogi: '',
          branchCfogi: '',
          nonBranchNogi: '',
          nonBranchCfogi: '',
          passive: '',
          giltiNogi: '',
          giltiFori: '',
          capitalGain: '',
        },
        {
          label: 'FTC',
          us: '',
          branchNogi: '',
          branchCfogi: '',
          nonBranchNogi: '',
          nonBranchCfogi: '',
          passive: '',
          giltiNogi: '',
          giltiFori: '',
          capitalGain: '',
        },
        {
          label: 'GBC',
          us: '',
          branchNogi: '',
          branchCfogi: '',
          nonBranchNogi: '',
          nonBranchCfogi: '',
          passive: '',
          giltiNogi: '',
          giltiFori: '',
          capitalGain: '',
        },
        {
          label: '',
          us: '',
          branchNogi: '',
          branchCfogi: '',
          nonBranchNogi: '',
          nonBranchCfogi: '',
          passive: '',
          giltiNogi: '',
          giltiFori: 'NPA ties AR/SAA',
          capitalGain: '',
        },
      ],

      arFilingId: null,
      arIssueId: null,
      irsIssueID: null,
      status: null,
      saaID: null,
      saaSubmission: null,
      docID: null,
      npa: null,
      saaChecked: false,
      epAdjText: 'ASC 740',
      isEPAdjRequired: null,
      isTBBSAdjRequired: null,
      dateCorptaxAdj: null,
    };
  }

  private parseAmount(value: unknown): number {
    if (value === null || value === undefined || value === '') {
      return 0;
    }
    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : 0;
    }
    const normalizedValue = String(value).replace(/,/g, '').replace(/\s/g, '').trim();
    if (!normalizedValue) {
      return 0;
    }
    const parsedValue = Number(normalizedValue);
    return Number.isFinite(parsedValue) ? parsedValue : 0;
  }

  onArFilingDropdownClick(existingArFilingId: string | null = null): void {
    const issueYearControl = this.existingIssueForm.get('issueYear');
    if (!issueYearControl) {
      return;
    }
    const issueYearValue = String(issueYearControl.value ?? '').trim();
    if (!issueYearValue) {
      issueYearControl.markAsTouched();
      issueYearControl.updateValueAndValidity({
        emitEvent: false,
      });

      this.toastService.error('Please select Year to load AR Filing IDs.');
      return;
    }
    if (!/^\d{4}$/.test(issueYearValue)) {
      issueYearControl.markAsTouched();
      issueYearControl.updateValueAndValidity({
        emitEvent: false,
      });

      this.toastService.error('Invalid date entered');
      return;
    }
    const issueYear = Number(issueYearValue);
    if (this.isArFilingLoading) {
      return;
    }
    if (this.loadedArFilingYear === issueYear) {
      if (existingArFilingId) {
        const matchedArFilingId = this.amendedDetails$().find(
          (item) => String(item.amendedID ?? '').trim() === existingArFilingId,
        );
        this.existingIssueForm.get('arFilingId')?.setValue(matchedArFilingId?.amendedID ?? null, {
          emitEvent: false,
        });
      }
      return;
    }
    this.isArFilingLoading = true;
    this.requestedArFilingYear = issueYear;
    this.amendedDetails$.set([]);
    this.arFilingPlaceholder = 'Select AR Filing ID';
    this.issuesService
      .loadFillingIDs(issueYear)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          if (this.requestedArFilingYear !== issueYear) {
            return;
          }
          this.isArFilingLoading = false;
          this.loadedArFilingYear = issueYear;
          const arFilingIds =
            response.success && Array.isArray(response.result) ? response.result : [];
          this.amendedDetails$.set(arFilingIds);

          if (arFilingIds.length === 0) {
            this.arFilingPlaceholder = 'No records found';
            if (!existingArFilingId) {
              this.toastService.error(`No records found for selected year ${issueYear}`);
            }
            return;
          }

          this.arFilingPlaceholder = 'Select AR Filing ID';
          if (existingArFilingId) {
            const matchedArFilingId = arFilingIds.find(
              (item) => String(item.amendedID ?? '').trim() === existingArFilingId,
            );
            this.existingIssueForm
              .get('arFilingId')
              ?.setValue(matchedArFilingId?.amendedID ?? null, {
                emitEvent: false,
              });
            this.existingIssueForm.markAsPristine();
            this.existingIssueForm.markAsUntouched();
          }
        },

        error: (error) => {
          if (this.requestedArFilingYear !== issueYear) {
            return;
          }
          console.error('Get AR Filing ID API error:', error);
          this.isArFilingLoading = false;
          this.loadedArFilingYear = issueYear;
          this.amendedDetails$.set([]);
          this.arFilingPlaceholder = 'No records found';
          if (!existingArFilingId) {
            this.toastService.error(`No records found for selected year ${issueYear}`);
          }
        },
      });
  }

  private subscribeToIssueYearChanges(): void {
    const issueYearControl = this.existingIssueForm.get('issueYear');
    const arFilingIdControl = this.existingIssueForm.get('arFilingId');
    if (!issueYearControl || !arFilingIdControl) {
      return;
    }
    issueYearControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value: unknown) => {
        const issueYearValue = String(value ?? '').trim();
        const isValidYear = /^\d{4}$/.test(issueYearValue);
        this.amendedDetails$.set([]);
        this.loadedArFilingYear = null;
        this.requestedArFilingYear = null;
        this.isArFilingLoading = false;
        arFilingIdControl.reset('', {
          emitEvent: false,
        });
        this.arFilingPlaceholder = isValidYear
          ? 'Select AR Filing ID'
          : 'Select Issue Year above to load AR Filing IDs into dropdown';
      });
  }

  closeForm(): void {
    this.existingIssueForm.reset();
  }
}
